import React, { useState, useEffect } from 'react';
import type { FC } from 'react';
import {
  Alert,
  AlertColor,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Snackbar,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  CircularProgress
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { getVouchers,createVoucher,updateVoucher,deleteVoucher,getServices } from '../services/voucherService';
import { Link } from 'react-router-dom';
import { number } from 'yup';

interface Voucher {
  voucher_id?: number;
  service_id: number | null;
  voucher_code: string;
  voucher_description: string;
  voucher_type: 'Percentage' | 'Fixed Price';
  voucher_discount: number | null;
  voucher_status: 'Active' | 'Inactive';
}

interface Service {
  service_id?: number;
  name: string;
}

type FormData = Omit<Voucher, 'id'>;

interface SnackbarState {
  open: boolean;
  message: string;
  severity: AlertColor;
}

const Vouchers: FC = () => {
   const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [vouchers, setVouchers] = useState<Voucher[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [open, setOpen] = useState<boolean>(false);
  const [editingVoucher, setEditingVoucher] = useState<Voucher | null>(null);
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: '',
    severity: 'success'
  });
   useEffect(() => {
      fetchVouchers();
      fetchServices();
    }, []);

  // const [services, setServices] = useState<Array<{ service_id: number; name: string }>>([]);

  const [formData, setFormData] = useState<FormData>({
    service_id:  null,
    voucher_code: '',
    voucher_description: '',
    voucher_type: 'Percentage',
    voucher_discount: 0,
    voucher_status: 'Active'
  });

  const fetchVouchers = async () => {
      try {
        setLoading(true);
        setError(''); // Clear any previous errors
        const response = await getVouchers();
        
        console.log('🔍 Full API response:', response);
        console.log('🔍 Response type:', typeof response);
        console.log('🔍 Response keys:', Object.keys(response || {}));
        
        // Try multiple possible data locations
        let voucherData = null;
        
        if (response?.data?.data && Array.isArray(response.data.data)) {
          voucherData = response.data.data;
          console.log('🔍 Using response.data.data (nested)');
        } else if (response?.data && Array.isArray(response.data)) {
          voucherData = response.data;
          console.log('🔍 Using response.data (direct array)');
        } else if (Array.isArray(response)) {
          voucherData = response;
          console.log('🔍 Using response (direct array)');
        } else if (response?.data?.vouchers && Array.isArray(response.data.vouchers)) {
          voucherData = response.data.vouchers;
          console.log('🔍 Using response.data.vouchers');
        } else if (response?.vouchers && Array.isArray(response.vouchers)) {
          voucherData = response.vouchers;
          console.log('🔍 Using response.vouchers');
        } else {
          console.log('🔍 Could not find Vouchers array in response');
          console.log('🔍 Available data:', response);
          voucherData = [];
        }
        
        console.log('🔍 Final Vouchers data:', voucherData);
        console.log('🔍 Vouchers data length:', voucherData?.length || 0);
        
        setVouchers(voucherData || []);
      } catch (err) {
        setError('Failed to fetch Vouchers. Please try again.');
        console.error('❌ Error fetching Vouchers:', err);
      } finally {
        setLoading(false);
      }
    };

    const fetchServices = async () => {
      try {
        setLoading(true);
        setError(''); // Clear any previous errors
        const response = await getServices();
        
        // Try multiple possible data locations
        let serviceData = null;
        
        if (response?.data?.data && Array.isArray(response.data.data)) {
          serviceData = response.data.data;
          console.log('🔍 Using response.data.data (nested)');
        } else if (response?.data && Array.isArray(response.data)) {
          serviceData = response.data;
          console.log('🔍 Using response.data (direct array)');
        } else if (Array.isArray(response)) {
          serviceData = response;
        } else if (response?.data?.services && Array.isArray(response.data.services)) {
          serviceData = response.data.services;
        } else if (response?.services && Array.isArray(response.services)) {
          serviceData = response.services;
        } else {
          serviceData = [];
        }
       
        setServices(serviceData || []);
      } catch (err) {
        setError('Failed to fetch services. Please try again.');
      } finally {
        setLoading(false);
      }
    };

  // const fetchServices = async () => {
  

  useEffect(() => {
    fetchVouchers();
    fetchServices();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target as HTMLInputElement;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseFloat(value) || 0 : value
    }));
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleSelectChange = (e: SelectChangeEvent<string | number>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: checked
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
  e.preventDefault();
  try {
    setLoading(true);

    const baseVoucherData = {
      service_id: formData.service_id || null,
      voucher_code: formData.voucher_code,
      voucher_description: formData.voucher_description,
      voucher_type: formData.voucher_type,
      voucher_discount: formData.voucher_discount ?? 0,
      voucher_status: formData.voucher_status
    };

    // ✅ Update
    if (editingVoucher) {
      if (!editingVoucher.voucher_id) {
        showSnackbar("Invalid voucher id", "error");
        return;
      }

      await updateVoucher(editingVoucher.voucher_id, baseVoucherData);
      showSnackbar("Voucher updated successfully", "success");
    } else {
      // ✅ Create
      await createVoucher(baseVoucherData);
      showSnackbar("Voucher created successfully", "success");
    }

    fetchVouchers();
    setOpen(false);

    // reset
    setFormData({
      service_id: null,
      voucher_code: "",
      voucher_description: "",
      voucher_type: "Percentage",
      voucher_discount: 0,
      voucher_status: "Active"
    });

  } catch (error) {
    console.error("Error saving voucher:", error);
    showSnackbar("Failed to save voucher", "error");
  } finally {
    setLoading(false);
  }
};


  const handleClose = () => {
    setOpen(false);
    setEditingVoucher(null);
  };

  const handleSnackbarClose = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleDelete = async (voucher_id: number) => {
      if (window.confirm('Are you sure you want to delete this voucher?')) {
        try {
          setLoading(true);
          await deleteVoucher(voucher_id,{voucher_status:'Delete'});
          showSnackbar('Voucher deleted successfully', 'success');
  
          // Refresh the banners list
          const response = await getVouchers();
          const data = Array.isArray(response) ? response : [];
          setVouchers(data);
          fetchVouchers();
        } catch (err) {
          console.error('Error deleting voucher:', err);
          showSnackbar('Failed to delete voucher', 'error');
        } finally {
          setLoading(false);
        }
      }
    };

  

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
       <Typography variant="h4" gutterBottom>
                                 Voucher
                       </Typography>
        
        <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={() => setOpen(true)}>
          Add Voucher
        </Button>
      </Box>

      {/* Dialog */}
      
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>{editingVoucher ? 'Edit Voucher' : 'Add New Voucher'}</DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: '1fr 1fr' }}>
              <TextField
                name="voucher_code"
                label="Code"
                value={formData.voucher_code}
                onChange={handleChange}
                fullWidth
                margin="normal"
                required
              />
              <FormControl fullWidth margin="normal">
                <InputLabel>Select Service</InputLabel>
                <Select
                  name="service_id"
                  value={formData.service_id || ''}
                  onChange={handleSelectChange}
                  label="Select Service"
                  required
                >
                  {services.map((item) => (
                    <MenuItem key={item.service_id} value={item.service_id}>
                      {item.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                name="voucher_description"
                label="Description"
                value={formData.voucher_description}
                onChange={handleChange}
                fullWidth
                margin="normal"
                multiline
                rows={3}
                sx={{ gridColumn: '1 / -1' }}
              />
              <FormControl fullWidth margin="normal">
                <InputLabel>Discount Type</InputLabel>
                <Select
                  name="voucher_type"
                  value={formData.voucher_type}
                  onChange={handleSelectChange}
                  label="Discount Type"
                  required
                >
                  <MenuItem value="Percentage">Percentage</MenuItem>
                  <MenuItem value="Fixed Price">Fixed Amount</MenuItem>
                </Select>
              </FormControl>
              <TextField
                name="voucher_discount"
                label={formData.voucher_type === 'Percentage' ? 'Discount Amount (%)' : `Discount Amount (${process.env.REACT_APP_SITE_CURRENCY})`}
                type="number"
                value={formData.voucher_discount}
                onChange={handleChange}
                fullWidth
                margin="normal"
                required
              />
             
              
              <FormControl fullWidth margin="normal">
                <InputLabel>Status</InputLabel>
                <Select
                  name="voucher_status"
                  value={formData.voucher_status}
                  onChange={handleSelectChange}
                  required
                >
                  <MenuItem value="Active">Active</MenuItem>
                  <MenuItem value="Inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
              
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} color="primary">
              Cancel
            </Button>
            <Button type="submit" color="primary" variant="contained">
              {editingVoucher ? 'Update' : 'Create'} Voucher
            </Button>
          </DialogActions>
        </form>
      </Dialog>

     

        <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 1 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Code</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Description</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Discount Type</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Discount</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>

              {vouchers.length === 0 && !loading ? (
                              <TableRow>
                                <TableCell colSpan={9} align="center" sx={{ py: 8 }}>
                                  <Typography variant="body1" color="text.secondary">
                                    No Vouchers found. Click "Add New Voucher" to create one.
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            ) : (
              vouchers.map((voucher) => (
                <TableRow key={voucher.voucher_id} hover>
                  <TableCell>{voucher.voucher_code}</TableCell>
                  <TableCell>{voucher.voucher_description}</TableCell>
                 
                 <TableCell>{voucher.voucher_type}</TableCell>
                   <TableCell>  {voucher.voucher_type === 'Percentage' 
                      ? `${voucher.voucher_discount}%` 
                      : `${process.env.REACT_APP_SITE_CURRENCY}${voucher.voucher_discount}`}
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={voucher.voucher_status} 
                      color={voucher.voucher_status === 'Active' ? 'success' : 'default'} 
                      size="small" 
                    />
                  </TableCell>
                  <TableCell>
                    
                      <IconButton onClick={() => {
                        setEditingVoucher(voucher);
                        setFormData(voucher);
                        setOpen(true);
                      }}>
                        <EditIcon fontSize='small' />
                      </IconButton>
                      <IconButton 
                        onClick={() => handleDelete(voucher.voucher_id!)} 
                        color="error" 
                        size="small"
                        disabled={loading}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    
                  </TableCell>
                </TableRow>
               ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      
      <Snackbar
              open={snackbar.open}
              autoHideDuration={6000}
              onClose={handleSnackbarClose}
              anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <Alert 
                onClose={handleSnackbarClose}
                severity={snackbar.severity}
                sx={{ width: '100%' }}
              >
                {snackbar.message}
              </Alert>
            </Snackbar>
    </Box>
  );
};

export default Vouchers;
